import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { 
  Button, 
  Input, 
  Logo, 
  DualPaneLayout,
  colors, 
  spacing, 
  typography 
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const ForgotPasswordScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { 
    handleBackToLogin: onBack,
    showNotification,
    navigateTo
  } = useAppContext();
  
  const { setPendingEmail } = useAppStore();
  const { t } = useLocale();
  const typo = useTypography();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string }>({});

  const validateForm = () => {
    const newErrors: { email?: string } = {};

    if (!email.trim()) {
      newErrors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('validation.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendResetEmail = async () => {
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        logError('Sending reset email to:', 'ForgotPasswordScreen');
        
        await apiService.forgotPassword(email);
        
        // Set pending email in centralized store
        setPendingEmail(email);
        
        // Show success notification
        showNotification('success', 'Reset Link Sent', 'Password reset code has been sent to your email.');
        
        // Navigate to reset password screen
        navigateTo('reset-password');
      } catch (error: any) {
        logError(error, 'ForgotPasswordScreen');
        
        // Handle error with unified error handling
        const { title, message, type } = handleApiError(error);
        showNotification(type, title, message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBackToLogin = () => {
    if (onBack) {
      onBack(); // Use context method
    }
  };

  return (
    <DualPaneLayout
      title="DISLIX"
      subtitle={t('auth.forgotTitle')}
    >
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          {/* Header */}
          <View style={styles.header}>
            <Logo size="medium" variant="white" />
          </View>

          {/* Main Content */}
          <ScrollView contentContainerStyle={styles.content}>
            <Text style={[styles.title, { fontFamily: typo.fontFamily.bold }]}>{t('auth.forgotTitle')}</Text>
            <Text style={[styles.subtitle, { fontFamily: typo.fontFamily.primary }]}>
              {t('auth.forgotSubtitle')}
            </Text>

            {/* Form */}
            <View style={styles.form}>
              <Input
                label={t('auth.email')}
                value={email}
                onChangeText={setEmail}
                placeholder={t('auth.emailPlaceholder')}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                error={errors.email}
                editable={!isLoading}
              />

              <Button
                title={t('auth.sendResetEmail')}
                onPress={handleSendResetEmail}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
                style={styles.sendButton}
              />
            </View>

            {/* Back to Login Link */}
            <View style={styles.backToLoginContainer}>
              <Text style={[styles.backToLoginText, { fontFamily: typo.fontFamily.primary }]}>{t('auth.rememberPassword')}</Text>
              <TouchableOpacity onPress={handleBackToLogin} disabled={isLoading}>
                <Text style={[styles.backToLoginLink, { fontFamily: typo.fontFamily.medium }, isLoading && styles.disabledText]}>{t('auth.signIn')}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  logoContainer: {
    alignItems: 'center',
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700',
    color: colors.textDark,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 22,
  },
  form: {
    marginBottom: spacing.xl,
  },
  sendButton: {
    width: '100%',
    marginTop: spacing.md,
  },
  backToLoginContainer: {
    alignItems: 'center',
    marginTop: spacing.md,
  },
  backToLoginText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
  },
  backToLoginLink: {
    color: colors.primary,
    fontWeight: '500',
  },
  disabledText: {
    color: colors.textLight,
  },
});
